import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import { APIMS } from "../../../../../../../../../config/apims";
import { APISD } from "../../../../../../../../../config/apisd";
import ToastError from "../../../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../../../NotificationToast/ToastSuccess";


const modalStyles = `
  .fase-grade-modal.module-modal-dialog {
    width: calc(100% - 28px);
    max-width: 920px;
  }

  .fase-grade-info {
    padding: 13px 15px;
    border: 1px solid #dfe7ff;
    border-radius: 11px;
    background: #f5f7ff;
    color: #405071;
    font-size: 13px;
    line-height: 1.6;
  }

  .fase-grade-section {
    margin-top: 20px;
  }

  .fase-grade-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .fase-grade-toolbar .module-form-label {
    margin-bottom: 0;
  }

  .fase-grade-select-all {
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: #3f5bea;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }

  .fase-grade-select-all:hover {
    color: #2f53ad;
    text-decoration: underline;
  }

  .fase-grade-helper {
    display: flex;
    min-height: 54px;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
    padding: 14px;
    border: 1px dashed #dce3f0;
    border-radius: 10px;
    background: #fafbfe;
    color: #8791a5;
    font-size: 13px;
    text-align: center;
  }

  .fase-grade-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 10px;
  }

  .fase-grade-card {
    position: relative;
    display: flex;
    min-width: 0;
    align-items: flex-start;
    gap: 10px;
    padding: 13px;
    border: 1px solid #e2e7f0;
    border-radius: 11px;
    background: #ffffff;
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease,
      transform 0.2s ease;
  }

  .fase-grade-card:hover {
    border-color: #b9c9f5;
    box-shadow: 0 8px 20px rgba(55, 83, 151, 0.09);
    transform: translateY(-1px);
  }

  .fase-grade-card.is-selected {
    border-color: #6a84dc;
    background: #f4f7ff;
    box-shadow: 0 0 0 2px rgba(67, 104, 197, 0.08);
  }

  .fase-grade-card input {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    margin-top: 2px;
    accent-color: #3f5bea;
    cursor: pointer;
  }

  .fase-grade-card-content {
    display: block;
    min-width: 0;
  }

  .fase-grade-card-title {
    display: block;
    color: #24314e;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.45;
  }

  .fase-grade-card-subtitle {
    display: block;
    margin-top: 4px;
    color: #758099;
    font-size: 11px;
    line-height: 1.45;
    white-space: normal;
    overflow-wrap: break-word;
  }

  .fase-grade-count {
    margin-top: 12px;
    color: #758099;
    font-size: 12px;
    line-height: 1.55;
  }

  .fase-grade-count b {
    color: #405071;
  }

  @media (max-width: 768px) {
    .fase-grade-list {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 520px) {
    .fase-grade-modal.module-modal-dialog {
      width: calc(100% - 18px);
      margin: 9px auto;
    }

    .fase-grade-toolbar {
      align-items: flex-start;
      flex-direction: column;
      gap: 5px;
    }

    .fase-grade-list {
      grid-template-columns: 1fr;
    }
  }
`;

const normalizeValue = (value) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .toUpperCase();

const getGradeId = (item) =>
  item?.ysb_grade_level_id || item?.grade_level_id || item?.id || "";

const getBranchCode = (item) =>
  item?.ysb_branch_id ||
  item?.branch_code ||
  item?.branch_id ||
  item?.branch ||
  "";

const getSchoolName = (item) =>
  item?.name_school || item?.school_name || item?.ysb_school_id || "-";

export default function ModalFaseGradeUpdate(props) {
  const token = localStorage.getItem("token");
  const storageLevel = localStorage.getItem("level") || "developer";
  const idUser = localStorage
    .getItem("id_admin")
    ?.replace(/[|&;$%@"<>()+,]/g, "");

  const [branches, setBranches] = useState([]);
  const [allGradeLevels, setAllGradeLevels] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedAllGradeIds, setSelectedAllGradeIds] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const loadMasterData = useCallback(async () => {
    setLoadingMaster(true);

    const branchRequestBody = {
      page: 1,
      limit: 1000,
      ascending: 1,
      search: "",
    };

    const gradeRequestBody = {
      page: 1,
      limit: 10000,
      ascending: 1,
      search: "",
      level: storageLevel,
    };

    try {
      const [branchResponse, gradeResponse] = await Promise.all([
        APIMS.get("/api/branches", {
          ...fetchParams,
          params: branchRequestBody,
          data: branchRequestBody,
        }),
        APIMS.get("/api/grade-levels", {
          ...fetchParams,
          params: gradeRequestBody,
          data: gradeRequestBody,
        }),
      ]);

      const branchRows = branchResponse?.data?.data || [];
      const gradeRows = gradeResponse?.data?.data || [];

      setBranches(
        [...branchRows].sort((a, b) =>
          String(
            a?.branch_name || a?.name_branch || a?.branch_code || ""
          ).localeCompare(
            String(
              b?.branch_name || b?.name_branch || b?.branch_code || ""
            ),
            "id",
            { numeric: true, sensitivity: "base" }
          )
        )
      );

      setAllGradeLevels(
        [...gradeRows].sort((a, b) => {
          const branchCompare = String(getBranchCode(a)).localeCompare(
            String(getBranchCode(b)),
            "id",
            { numeric: true, sensitivity: "base" }
          );

          if (branchCompare !== 0) {
            return branchCompare;
          }

          const schoolCompare = String(getSchoolName(a)).localeCompare(
            String(getSchoolName(b)),
            "id",
            { numeric: true, sensitivity: "base" }
          );

          if (schoolCompare !== 0) {
            return schoolCompare;
          }

          return String(a?.name_level || "").localeCompare(
            String(b?.name_level || ""),
            "id",
            { numeric: true, sensitivity: "base" }
          );
        })
      );
    } catch (error) {
      setBranches([]);
      setAllGradeLevels([]);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal mengambil data cabang dan tingkatan",
      });
    } finally {
      setLoadingMaster(false);
    }
  }, [fetchParams, storageLevel]);

  useEffect(() => {
    if (!props?.show) {
      return;
    }

    const currentAssignedIds = (props?.allAssignedGrades || [])
      .map((grade) => getGradeId(grade))
      .filter(Boolean);

    setSelectedAllGradeIds(currentAssignedIds);
    setSelectedBranch(getBranchCode(props?.dataUpdate) || "");
    setIsSubmitting(false);
    loadMasterData();
  }, [
    loadMasterData,
    props?.allAssignedGrades,
    props?.dataUpdate,
    props?.show,
  ]);

  const filteredGradeLevels = useMemo(() => {
    if (!selectedBranch) {
      return [];
    }

    return allGradeLevels.filter(
      (grade) =>
        normalizeValue(getBranchCode(grade)) ===
        normalizeValue(selectedBranch)
    );
  }, [allGradeLevels, selectedBranch]);

  const selectedCurrentBranchCount = useMemo(
    () =>
      filteredGradeLevels.filter((grade) =>
        selectedAllGradeIds.some(
          (selectedId) =>
            normalizeValue(selectedId) === normalizeValue(getGradeId(grade))
        )
      ).length,
    [filteredGradeLevels, selectedAllGradeIds]
  );

  const isAllCurrentBranchChecked = useMemo(() => {
    if (filteredGradeLevels.length === 0) {
      return false;
    }

    return filteredGradeLevels.every((grade) =>
      selectedAllGradeIds.some(
        (selectedId) =>
          normalizeValue(selectedId) === normalizeValue(getGradeId(grade))
      )
    );
  }, [filteredGradeLevels, selectedAllGradeIds]);

  const handleGradeCheck = (gradeId) => {
    const normalizedGradeId = normalizeValue(gradeId);

    setSelectedAllGradeIds((previous) => {
      const alreadySelected = previous.some(
        (id) => normalizeValue(id) === normalizedGradeId
      );

      if (alreadySelected) {
        return previous.filter(
          (id) => normalizeValue(id) !== normalizedGradeId
        );
      }

      return [...previous, gradeId];
    });
  };

  const handleSelectAllCurrentBranch = () => {
    const currentBranchGradeIds = filteredGradeLevels
      .map((grade) => getGradeId(grade))
      .filter(Boolean);

    if (isAllCurrentBranchChecked) {
      const currentBranchIdSet = new Set(
        currentBranchGradeIds.map((id) => normalizeValue(id))
      );

      setSelectedAllGradeIds((previous) =>
        previous.filter(
          (id) => !currentBranchIdSet.has(normalizeValue(id))
        )
      );
      return;
    }

    setSelectedAllGradeIds((previous) => {
      const next = [...previous];
      const nextIdSet = new Set(next.map((id) => normalizeValue(id)));

      currentBranchGradeIds.forEach((gradeId) => {
        if (!nextIdSet.has(normalizeValue(gradeId))) {
          next.push(gradeId);
          nextIdSet.add(normalizeValue(gradeId));
        }
      });

      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!props?.faseId) {
      ToastError.fire({
        icon: "error",
        title: "ID fase belum tersedia",
      });
      return;
    }

    if (selectedAllGradeIds.length === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu tingkatan untuk fase ini",
      });
      return;
    }

    if (!idUser) {
      ToastError.fire({
        icon: "error",
        title: "User ID belum tersedia. Silakan login ulang.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await APISD.post(
        `/api/fases/store-grades/${props.faseId}`,
        {
          ysb_grade_level_ids: selectedAllGradeIds,
          user_id: idUser,
        },
        fetchParams
      );

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data?.status === 200 ||
        response?.data?.status === 201
      ) {
        ToastSuccess.fire({
          icon: "success",
          title:
            response?.data?.message ||
            "Konfigurasi tingkatan fase berhasil diperbarui",
        });

        await props?.GetResponseData?.();
        props?.onHide?.();
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal memperbarui konfigurasi tingkatan fase",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{modalStyles}</style>

      <Modal
        {...props}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="module-modal-dialog fase-grade-modal"
        contentClassName="module-modal-content"
      >
        <form onSubmit={handleSubmit}>
          <div className="module-modal-header">
            <div>
              <h5 className="module-modal-title">Update Tingkatan Fase</h5>
              
            </div>

            <button
              type="button"
              className="module-modal-close"
              onClick={props?.onHide}
              disabled={isSubmitting}
              aria-label="Tutup modal"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <Modal.Body className="module-modal-body mt-2 mx-2">
            <div className="fase-grade-info">
              Atur kembali tingkatan yang masuk ke fase{" "}
              <b>{props?.faseName || "-"}</b>. 
              {/* Pilihan dari cabang lain tetap
              tersimpan ketika cabang diganti. */}
            </div>

            <div className="fase-grade-section">
              <label
                className="module-form-label"
                htmlFor="update-fase-grade-branch"
              >
                Cabang
              </label>

              <select
                id="update-fase-grade-branch"
                name="ysb_branch_id"
                value={selectedBranch}
                onChange={(event) => setSelectedBranch(event.target.value)}
                className="module-form-input"
                disabled={loadingMaster || isSubmitting}
              >
                <option value="">Pilih Cabang...</option>

                {branches.map((branch, index) => {
                  const branchCode =
                    branch?.branch_code ||
                    branch?.ysb_branch_id ||
                    branch?.id;
                  const branchName =
                    branch?.branch_name ||
                    branch?.name_branch ||
                    branchCode ||
                    "-";

                  return (
                    <option
                      key={branch?.id || branchCode || index}
                      value={branchCode}
                    >
                      {branchName}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="fase-grade-section">
              <div className="fase-grade-toolbar">
                <label className="module-form-label">Tingkatan</label>

                {selectedBranch && filteredGradeLevels.length > 0 && (
                  <button
                    type="button"
                    className="fase-grade-select-all"
                    onClick={handleSelectAllCurrentBranch}
                    disabled={isSubmitting}
                  >
                    {isAllCurrentBranchChecked
                      ? "Batalkan semua cabang ini"
                      : "Pilih semua cabang ini"}
                  </button>
                )}
              </div>

              {!selectedBranch ? (
                <div className="fase-grade-helper">
                  Silakan pilih cabang untuk melihat tingkatannya.
                </div>
              ) : loadingMaster ? (
                <div className="fase-grade-helper">
                  Memuat data tingkatan...
                </div>
              ) : filteredGradeLevels.length === 0 ? (
                <div className="fase-grade-helper">
                  Tingkatan tidak ditemukan untuk cabang ini.
                </div>
              ) : (
                <>
                  <div className="fase-grade-list">
                    {filteredGradeLevels.map((grade, index) => {
                      const gradeId = getGradeId(grade);
                      const isSelected = selectedAllGradeIds.some(
                        (selectedId) =>
                          normalizeValue(selectedId) ===
                          normalizeValue(gradeId)
                      );

                      return (
                        <label
                          key={gradeId || index}
                          className={`fase-grade-card ${
                            isSelected ? "is-selected" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isSubmitting}
                            onChange={() => handleGradeCheck(gradeId)}
                          />

                          <span className="fase-grade-card-content">
                            <span className="fase-grade-card-title">
                              Tingkatan {grade?.name_level || "-"}
                            </span>

                            <span className="fase-grade-card-subtitle">
                              {getSchoolName(grade)}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  <div className="fase-grade-count">
                    <b>{selectedCurrentBranchCount}</b> tingkatan dipilih pada
                    cabang ini. Total seluruh cabang:{" "}
                    <b>{selectedAllGradeIds.length}</b>.
                  </div>
                </>
              )}
            </div>
          </Modal.Body>

          <div className="module-modal-footer">
            <button
              type="button"
              className="module-modal-cancel"
              onClick={props?.onHide}
              disabled={isSubmitting}
            >
              Batal
            </button>

            <button
              type="submit"
              className="module-modal-submit"
              disabled={
                isSubmitting ||
                loadingMaster ||
                selectedAllGradeIds.length === 0
              }
            >
              {isSubmitting && <span className="module-button-spinner" />}
              {isSubmitting ? "Memperbarui..." : "Perbarui"}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
